import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  tooltipClasses,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles, styled } from "@mui/styles";
import cssVariables from "../../theme/cssVariables"
import { useDispatch, useSelector } from "react-redux";

import CloseIcon from "../../assets/images/plus-circle-fill.svg";
import { ReactComponent as CaseIcon } from "../../icons/case.svg";
import { ReactComponent as PeopleIcon } from "../../icons/people.svg";
import { ReactComponent as CalendarIcon } from "../../icons/calendar-checked.svg";
import { ReactComponent as PercentIcon } from "../../icons/percent-circle.svg";
import { ReactComponent as ProrateIcon } from "../../icons/prorate.svg";
import { ReactComponent as StarIcon } from "../../icons/star.svg";

import { SCORETYPE } from '../../views/app/kpi/calibration/EditCalibration'

import * as Util from "../../services/global.service"

const BootstrapTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: "#343A40",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#343A40",
    color: "#FFFFFF",
    padding: "11px 15px",
  },
}))

const DetailDialog = (props) => {
  const { detailData, openConfirm, confirmChange, canEdit, scoreType } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const calibrationState = useSelector((state) => state.Calibration)

  const styled = {
    "& .MuiDialog-container": {
      justifyContent: "flex-end",
      height: "auto"
    },
    "&#detail-dialog-container > .MuiDialog-container > .MuiPaper-root": {
      width: "100%",
      height: "100vh",
      margin: 0,
      borderRadius: "0px"
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "transparent!important"
    }
  }

  const useStyles = makeStyles({
    primaryColorSvg: {
      "& > path": {
        stroke: cssVariables.color.primary,
      }
    },
    starIconSvg: {
      width: "20px",
      height: "20px"
    },
    starIconSelectedSvg: {
      width: "20px",
      height: "20px",
      "& > path": {
        fill: "#FFF",
      }
    },
    boxStyled: {
      borderRadius: "10px!important",
      border: "1px solid #DADADA",
      boxShadow: "none!important",
      width: "100%",
      minHeight: "200px",
      padding: "20px"
    },
    ratingChanged: {
      color: "#4085C9!important"
    },
    ratingNotChanged: {
      color: "#404349"
    }
  })
  const classes = useStyles()

  const handleClose = () => {
    props.setValue(false)
  }
  const [selectedRating, setSelectedRating] = React.useState("")
  const [selectedRatingTmp, setSelectedRatingTmp] = React.useState(selectedRating)
  const [defaultRating, setDefaultRating] = React.useState("")
  const [ratingList, setRatingList] = React.useState([])
  const handleConfirmChangeRating = (value) => {
    // confirm before change
    openConfirm(true)
    setSelectedRatingTmp(value)
  }

  const formatExpDate = (value) => {
    let formatted = value.split('y').join(',').split('m').join(',').split('d').join(',').split(',')
    return `${formatted?.[0] ?? 0}y ${formatted?.[1] ?? 0}m ${formatted?.[2] ?? 0}d`
  }

  const formatEvalType = (value) => {
    if (value === "evaluate") {
      return "total"
    } else {
      return value
    }
  }

  // useEffect
  useEffect(() => {
    // set rating list dropdown
    let tmpDropdown = []
    detailData?.classification_dropdown?.forEach((item, index) => {
      tmpDropdown.push({
        name: `${item?.seq} - ${item?.description}` ?? "",
        value: item?.id ?? index
      })
    })
    setRatingList(tmpDropdown)
    setDefaultRating(detailData?.calibration_classification?.id ?? "")
    let changedData = { ...calibrationState.changedCalibrationData }
    if (changedData[detailData?.employee?.serial] !== undefined) {
      setSelectedRating(changedData[detailData.employee.serial] ?? "")
    } else {
      setSelectedRating(detailData?.calibration_classification?.id ?? "")
    }
  }, [detailData, calibrationState])

  // do change classification after confirm
  useEffect(() => {
    if (confirmChange) {
      setSelectedRating(selectedRatingTmp)
    }
  }, [confirmChange])

  useEffect(() => {
    if (selectedRating) {
      let empSerial = detailData?.employee?.serial
      let changedData = {
        [empSerial]: selectedRating
      }
      dispatch({
        type: "SET_CHANGED_CALIBRATION_DATA",
        payload: changedData,
      })
    }
  }, [selectedRating])
  // end useEffect

  return (
    <React.Fragment>
      <Dialog
        open={props.open}
        onClose={handleClose}
        id="detail-dialog-container"
        sx={styled}
        aria-labelledby="detail-dialog-title"
        aria-describedby="detail-dialog-description"
      >
        <DialogTitle id="form-add-dialog-title" sx={{ display: "flex", alignItems: "center" }}>
          <Avatar src={detailData?.employee?.photo ?? undefined} sx={{ mr: 1 }} />
          <Typography fontSize="18px" fontWeight="600" color="#5DA196"> {detailData?.employee?.fullname ?? "-"} </Typography>
          <BootstrapTooltip
            title={t("label.close")}
            placement="bottom"
          >
            <Box sx={{ position: "absolute", right: 0, margin: "20px 18px 0  0", top: 0 }} className="clickable" onClick={handleClose}><img src={CloseIcon} alt="browser" /></Box>
          </BootstrapTooltip>
        </DialogTitle>
        <DialogContent>
          <Grid container mb={0.5}>
            <Grid item display="flex" alignItems="center">
              <BootstrapTooltip
                title={t("calibration.label.position")}
                placement="top"
              >
                <CaseIcon />
              </BootstrapTooltip>
              <Typography fontSize="16px" fontWeight="400" color="#404349" sx={{ ml: 1 }}> {detailData?.employee?.position ?? "-"} ({formatExpDate(detailData?.employee?.emp_exp ?? "0y0m0d")}) </Typography>
            </Grid>
          </Grid>
          <Grid container mb={0.5}>
            <Grid item display="flex" alignItems="center">
              <BootstrapTooltip
                title={t("calibration.label.organization")}
                placement="top"
              >
                <PeopleIcon />
              </BootstrapTooltip>
              <Typography fontSize="16px" fontWeight="400" color="#404349" sx={{ ml: 1 }}> {detailData?.employee?.organization ?? "-"} </Typography>
            </Grid>
          </Grid>
          <Grid container mb={0.5}>
            <Grid item display="flex" alignItems="center">
              <BootstrapTooltip
                title={t("calibration.label.startDate")}
                placement="top"
              >
                <CalendarIcon />
              </BootstrapTooltip>
              <Typography fontSize="16px" fontWeight="400" color="#404349" sx={{ ml: 1 }}> {Util.dateDayFormatAdmin(detailData?.employee?.position_start_at)} ({formatExpDate(detailData?.employee?.pos_exp ?? "0y0m0d")}) </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Grid container my={1}>
            <Grid item display="flex" alignItems="center">
            </Grid>
          </Grid>
          <Grid container my={1}>
            <Grid item xs={6} display="flex">
              <Typography fontSize="14px" fontWeight="400" color="#AAA"> {t("calibration.label.ratingClassification")} </Typography>
            </Grid>
            <Grid item xs={6} display="flex">
              <Typography fontSize="14px" fontWeight="400" color="#AAA"> {t("calibration.label.calibrationClassification")}</Typography>
            </Grid>
          </Grid>
          <Grid container my={1}>
            <Grid item xs={6} display="flex" alignItems="center">
              <Typography fontSize="16px" fontWeight="400"> {detailData?.rating_classification?.id !== "" ? `${detailData?.rating_classification?.seq ?? 0} - ${detailData?.rating_classification?.description ?? "-"}` : "0"}</Typography>
            </Grid>
            <Grid item xs={6} display="flex" alignItems="center">
              {canEdit ?
                <Select
                  id="calibration-select"
                  inputlabelprops={{ shrink: false }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  className="white-bg"
                  value={selectedRating}
                  sx={{ height: "33px" }}
                  classes={{ select: defaultRating !== selectedRating ? classes.ratingChanged : classes.ratingNotChanged }}
                  renderValue={
                    selectedRating !== "" ? undefined : () => <Typography fontSize="16px" fontWeight="400">{t("adminWorkflow.label.selectTheType")}</Typography>
                  }
                >
                  {ratingList?.length > 0 ? (
                    ratingList.map((item) => {
                      return (
                        <MenuItem value={item.value} key={item.value} onClick={() => handleConfirmChangeRating(item.value)} sx={{ color: "red" }}>
                          {item.name}
                        </MenuItem>
                      )
                    })
                  ) : (
                    <MenuItem value="">{t("label.noData")}</MenuItem>
                  )}
                </Select>
                : <Typography fontSize="16px" fontWeight="400"> {`${detailData?.calibration_classification?.seq ?? 0} - ${detailData?.calibration_classification?.description ?? "-"}`} </Typography>
              }
            </Grid>
          </Grid>
          <Grid container mt={2}>
            {
              detailData?.form_evaluate?.length > 0 ?
                detailData.form_evaluate.map((formEval, index) => {
                  return (
                    <Grid item xs={12} display="flex" key={`form-eval-${index}`} mb="20px">
                      <Paper classes={{ root: classes.boxStyled }}>
                        <Grid container mb={1}>
                          <Grid item>
                            <Typography fontSize="18px" fontWeight="500"> {formEval?.employee?.fullname ?? "-"} </Typography>
                          </Grid>
                        </Grid>
                        <Grid container mb={1}>
                          <Grid item display="flex" alignItems="center">
                            <BootstrapTooltip
                              title={t("calibration.label.position")}
                              placement="top"
                            >
                              <CaseIcon />
                            </BootstrapTooltip>
                            <Typography fontSize="16px" fontWeight="400" color="#404349" sx={{ ml: 1 }}> {formEval?.employee?.position ?? "-"} </Typography>
                          </Grid>
                        </Grid>
                        <Grid container mb={1}>
                          <Grid item display="flex" alignItems="center">
                            <BootstrapTooltip
                              title={t("calibration.label.organization")}
                              placement="top"
                            >
                              <PeopleIcon />
                            </BootstrapTooltip>
                            <Typography fontSize="16px" fontWeight="400" color="#404349" sx={{ ml: 1 }}> {formEval?.employee?.organization ?? "-"} </Typography>
                          </Grid>
                        </Grid>
                        {
                          formEval?.ratio &&
                          <Grid container mb={1}>
                            <Grid item display="flex" alignItems="center">
                              <BootstrapTooltip
                                title={t("calibration.label.assignmentRatio")}
                                placement="top"
                              >
                                <PercentIcon />
                              </BootstrapTooltip>
                              <Typography fontSize="16px" fontWeight="500" color="#5DA196" sx={{ ml: 1 }}> {formEval?.ratio ?? "-"}% </Typography>
                            </Grid>
                          </Grid>
                        }
                        {
                          formEval?.options?.prorate &&
                          <Grid container mb={2}>
                            <Grid item display="flex" alignItems="center">
                              <BootstrapTooltip
                                title={t("calibration.label.prorate")}
                                placement="top"
                              >
                                <ProrateIcon />
                              </BootstrapTooltip>
                              <Typography fontSize="16px" fontWeight="500" color="#5DA196" sx={{ ml: 1 }}> {formEval?.options?.prorate ?? "-"}% </Typography>
                            </Grid>
                          </Grid>
                        }


                        {
                          formEval?.options?.evaluator_grade &&
                          <Grid container mb={2}>
                            <Grid item display="flex" alignItems="center">
                              <Typography fontSize="16px" fontWeight="500" sx={{ ml: 1 }}>Evaluator Grade:  {formEval?.options?.evaluator_grade ?? "-"}</Typography>
                            </Grid>
                          </Grid>
                        }

                        <Grid container mb={1}>
                          <Grid item display="flex" alignItems="center">
                            <Box py="8px" px="20px" sx={{ "max-width": "100%", width: "max-content", border: formEval?.status?.code === "not_started" ? "1px solid #DADADA" : "none", "border-radius": "5px", background: formEval?.status?.code === "in_progress" ? "#EFB76D1A" : formEval?.status?.code === "pending" ? "#FEF3EA" : formEval?.status?.code === "completed" || formEval?.status?.code === "approved" ? "#5DA1961A" : formEval?.status?.code === "deleted" ? "#8282821A" : "1px solid #DADADA" }} >
                              <Typography fontSize={16} lineHeight="24px" fontWeight={500} color={formEval?.status?.code === "in_progress" ? "#EFB76D" : formEval?.status?.code === "pending" ? "#F28026" : formEval?.status?.code === "completed" || formEval?.status?.code === "approved" ? "#5DA196" : formEval?.status?.code === "deleted" ? "#828282" : "#6B6B6B"} sx={{ "text-align": "center", "white-space": "nowrap", width: "100%", overflow: "hidden", "text-overflow": "ellipsis" }}>
                                {formEval?.status.name}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>


                        <Grid container mb={1}>
                          <Grid item display="flex" alignItems="center">
                            <Typography fontSize="14px" fontWeight="400" color="#AAAAAA"> {t("calibration.label.evaluator")} </Typography>
                          </Grid>
                        </Grid>
                        <Grid container mb={2}>
                          <Grid item display="flex" alignItems="center" xs="auto">
                            <Avatar src={detailData?.employee?.photo ?? undefined} sx={{ mr: 1 }} />
                          </Grid>
                          <Grid item display="flex" xs={10} flexDirection="column">
                            <Grid container item display="flex" alignItems="center">
                              <Typography fontSize="16px" fontWeight="600"> {formEval?.evaluator?.fullname ?? "-"} </Typography>
                            </Grid>
                            <Grid container item display="flex" alignItems="center">
                              <Typography fontSize="16px" fontWeight="400" sx={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}> {formEval?.evaluator?.position ?? "-"} ({formEval?.evaluator?.organization ?? "-"})</Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid container mb={1} display="flex" justifyContent="space-between" sx={{ rowGap: "20px" }}>
                          {
                            formEval?.rating?.length > 0 ?
                              formEval.rating.map((rating, index) => {
                                let score = isNaN(parseFloat(rating?.value?.evaluator).toFixed(props?.ratingDecimal ?? 2)) ? "-" : parseFloat(rating?.value?.evaluator).toFixed(props?.ratingDecimal ?? 2)
                                if (scoreType === SCORETYPE.PERCENTAGE) score += '%'

                                let label = ''
                                if (scoreType === SCORETYPE.PERCENTAGE) {

                                  if (rating?.type !== "custom") {
                                    let format = formatEvalType(rating?.type)
                                    if (format && format !== 'total') label = t('calibration.label.' + format)
                                    else label = t("calibration.label.score")
                                  } else {
                                    label = rating?.options?.custom_template_name
                                  }

                                } else {
                                  if (rating?.type !== "custom") {
                                    let format = formatEvalType(rating?.type)
                                    if (format) label = t('calibration.label.' + format)
                                  } else {
                                    label = rating?.options?.custom_template_name
                                  }
                                  if (label) label += ' '
                                  label += t("calibration.label.score")
                                }

                                return (
                                  <Grid item display="flex" key={`rating-item-${index}`}>
                                    <Box sx={{ borderRadius: "5px", width: "128px", minHeight: "57px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", border: rating?.type === detailData?.component_type ? "1px solid #5DA196" : "1px solid #DADADA", backgroundColor: rating?.type === detailData?.component_type ? cssVariables.color.primary : "#FFF", textAlign: "center" }}>
                                      <Box sx={{ display: "flex", alignItems: "center" }}>
                                        {scoreType !== SCORETYPE.PERCENTAGE && <StarIcon className={rating?.type === detailData?.component_type ? classes.starIconSelectedSvg : classes.starIconSvg} />}

                                        <Typography fontSize="18px" fontWeight="500" sx={{ ml: 1 }} color={rating?.type === detailData?.component_type ? "#FFF" : "#404349"}>{score}</Typography>
                                      </Box>
                                      <Typography fontSize="12px" fontWeight="400" color={rating?.type === detailData?.component_type ? "#FFF" : "#AAAAAA"} sx={{ mt: "2px" }}>
                                        {label}</Typography>
                                    </Box>
                                  </Grid>
                                )
                              })
                              : <></>
                          }
                        </Grid>
                      </Paper>
                    </Grid>
                  )
                })
                : <></>
            }
          </Grid>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};
export default DetailDialog;
